// src/shared/payment.js
//
// The per-deal PAYMENT PLAN — when the money is due, as opposed to what was
// bought (shared/pricing.js) or at what cadence a SKU bills (the catalog).
//
// This is deliberately NOT a catalog property: the same line item is paid
// differently by different customers ("50% on signature, 50% on go-live" vs
// "due on receipt"). Billing FREQUENCY belongs to the SKU; the payment
// SCHEDULE belongs to the deal.
//
// Two consumers that must never disagree:
//   • the agreement text the client signs (percentages — the selection isn't
//     known while the rep is building, so dollars can't be stated there)
//   • the frozen order (percentages resolved to dollars against the signed total)
//
// CommonJS so the Node server can require() it and the CRA client can import it.

// A plan is either "everything due on receipt" (the default, no extra document
// language) or an ordered list of milestones expressed in percent.
const DUE_ON_RECEIPT = 'due_on_receipt';
const MILESTONES     = 'milestones';

// Presets offered in the builder. `custom` starts from the 50/50 shape.
const PRESETS = {
  [DUE_ON_RECEIPT]: { label: 'Due on receipt (default)', milestones: [] },
  split_50_50: {
    label: '50% on signature / 50% on completion',
    milestones: [
      { label: 'Upon signature of this document', percent: 50 },
      { label: 'Upon completion',                 percent: 50 },
    ],
  },
  split_50_50_annual: {
    label: '50% on signature / 50% on submission',
    milestones: [
      { label: 'Upon signature of this document',    percent: 50 },
      { label: 'Upon completion of the submission',  percent: 50 },
    ],
  },
};

function round2(n) { return Math.round(n * 100) / 100; }

// Coerce whatever the UI/draft holds into a predictable shape. Never throws.
function normalizePaymentPlan(plan) {
  if (!plan || typeof plan !== 'object') return { type: DUE_ON_RECEIPT, milestones: [] };
  if (plan.type !== MILESTONES) return { type: DUE_ON_RECEIPT, milestones: [] };
  const milestones = (Array.isArray(plan.milestones) ? plan.milestones : [])
    .map(m => ({
      label:   String((m && m.label) || '').trim(),
      percent: Math.max(0, Number((m && m.percent) || 0)),
    }))
    .filter(m => m.label || m.percent > 0);
  if (!milestones.length) return { type: DUE_ON_RECEIPT, milestones: [] };
  return { type: MILESTONES, milestones };
}

// Percentages must add to 100 or the schedule doesn't describe the whole deal.
function paymentPlanIssues(plan) {
  const p = normalizePaymentPlan(plan);
  if (p.type !== MILESTONES) return [];
  const issues = [];
  const total = round2(p.milestones.reduce((a, m) => a + m.percent, 0));
  if (total !== 100) issues.push(`Milestone percentages total ${total}% — they must add up to 100%.`);
  if (p.milestones.some(m => !m.label)) issues.push('Every milestone needs a description.');
  return issues;
}

// Document language. Percent-only on purpose: while the rep is building, the
// client hasn't chosen their options yet, so no dollar figure would be honest.
function paymentPlanBullets(plan) {
  const p = normalizePaymentPlan(plan);
  if (p.type !== MILESTONES) return [];
  return [
    'The total above will be invoiced according to the following schedule:',
    ...p.milestones.map(m => `${m.percent}% — ${m.label}`),
  ];
}

// Resolve to money once the signed total is known. The final milestone absorbs
// any rounding remainder so the instalments always sum to the total exactly —
// an invoicing schedule that doesn't reconcile is worse than none.
function resolvePaymentPlan(plan, grandTotal) {
  const p = normalizePaymentPlan(plan);
  const total = Number(grandTotal) || 0;
  if (p.type !== MILESTONES) {
    return { type: DUE_ON_RECEIPT, milestones: [{ label: 'Due on receipt', percent: 100, amount: round2(total) }] };
  }
  let allocated = 0;
  const milestones = p.milestones.map((m, i) => {
    const isLast = i === p.milestones.length - 1;
    const amount = isLast ? round2(total - allocated) : round2(total * (m.percent / 100));
    if (!isLast) allocated = round2(allocated + amount);
    return { label: m.label, percent: m.percent, amount };
  });
  return { type: MILESTONES, milestones };
}

module.exports = {
  DUE_ON_RECEIPT,
  MILESTONES,
  PRESETS,
  normalizePaymentPlan,
  paymentPlanIssues,
  paymentPlanBullets,
  resolvePaymentPlan,
};
